import { useCallback, useEffect, useState } from "react";
import axiosInstance from "../Backend/IghotaVirtualLearning/axiosInstance.js";
import supabase from "../config/supabase";
import { useAuth } from "@/contexts/useAuth";

// Grading happens server-side and doesn't require auth, but saving the
// attempt does. An unauthenticated test-taker's graded result is stashed
// here so it survives a login/signup round trip instead of forcing a retake.
// It's flushed both by a global, always-mounted component (so it saves no
// matter which page an OAuth redirect lands on) and by the benchmark-test
// page itself (so it can restore the result on screen). Both call
// claimPendingResult(), which reads-then-removes synchronously so only one
// of them ever actually performs the save, even if both run "at once".
const PENDING_RESULT_KEY = "ighota:pendingDiagnosticResult";
// Whichever caller wins the claim above stashes the now-saved result here so
// the other one (usually the benchmark-test page) can still display it.
const LAST_SYNCED_RESULT_KEY = "ighota:lastSyncedDiagnosticResult";

function claimPendingResult() {
	try {
		const raw = sessionStorage.getItem(PENDING_RESULT_KEY);
		if (!raw) return null;
		sessionStorage.removeItem(PENDING_RESULT_KEY);
		return JSON.parse(raw);
	} catch {
		return null;
	}
}

function writePendingResult(benchmarkTestId, result) {
	try {
		sessionStorage.setItem(
			PENDING_RESULT_KEY,
			JSON.stringify({ benchmarkTestId: String(benchmarkTestId), result }),
		);
	} catch {
		// sessionStorage unavailable (e.g. private browsing) — nothing to persist
	}
}

function clearPendingResult() {
	try {
		sessionStorage.removeItem(PENDING_RESULT_KEY);
	} catch {
		// ignore
	}
}

function writeLastSyncedResult(result) {
	try {
		sessionStorage.setItem(LAST_SYNCED_RESULT_KEY, JSON.stringify(result));
	} catch {
		// ignore
	}
}

function consumeLastSyncedResult() {
	try {
		const raw = sessionStorage.getItem(LAST_SYNCED_RESULT_KEY);
		if (!raw) return null;
		sessionStorage.removeItem(LAST_SYNCED_RESULT_KEY);
		return JSON.parse(raw);
	} catch {
		return null;
	}
}

// Hook exposing the diagnostic-test loop: fetch the active benchmark test,
// start an attempt, submit answers for server-side grading, and persist the
// graded result (with recommendations) to Supabase for the logged-in user.
export function useDiagnosticTest() {
	const { user } = useAuth();
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState(null);

	const getActiveBenchmarkTest = useCallback(async () => {
		const response = await axiosInstance.get(
			"/benchmark-tests?pagination[limit]=1&populate=questions"
		);
		return response?.data?.data?.[0] || null;
	}, []);

	const getAvailableSubjects = useCallback(async (benchmarkTestId) => {
		const response = await axiosInstance.get(
			`/benchmark-tests/${benchmarkTestId}/subjects`
		);
		return response?.data?.data;
	}, []);

	const startTest = useCallback(async (benchmarkTestId, subjectIds) => {
		const response = await axiosInstance.post(
			`/benchmark-tests/${benchmarkTestId}/start`,
			{ subjectIds }
		);
		return response?.data?.data;
	}, []);

	const submitTest = useCallback(async (benchmarkTestId, answers) => {
		const response = await axiosInstance.post(
			`/benchmark-tests/${benchmarkTestId}/submit`,
			{ answers }
		);
		return response?.data?.data;
	}, []);

	const saveAttempt = useCallback(
		async (benchmarkTestId, result) => {
			if (!user) throw new Error("User not authenticated");

			const { data, error: supabaseError } = await supabase
				.from("diagnostic_attempts")
				.insert({
					user_id: user.id,
					benchmark_test_id: String(benchmarkTestId),
					subject_scores: result.subjectScores,
					recommendations: result.recommendations,
				})
				.select()
				.single();

			if (supabaseError) throw supabaseError;
			return data;
		},
		[user]
	);

	// Grades the test unconditionally, then only persists the attempt if the
	// test-taker is logged in. When they're not, the result is stashed locally
	// (see savePendingResult) and `saved: false` tells the caller to prompt for
	// login/signup instead of losing the graded result outright.
	const takeDiagnosticTest = useCallback(
		async (benchmarkTestId, answers) => {
			setLoading(true);
			setError(null);
			try {
				const result = await submitTest(benchmarkTestId, answers);
				if (user) {
					await saveAttempt(benchmarkTestId, result);
					clearPendingResult();
					return { result, saved: true };
				}
				writePendingResult(benchmarkTestId, result);
				return { result, saved: false };
			} catch (err) {
				setError(err.message || "Failed to grade diagnostic test");
				throw err;
			} finally {
				setLoading(false);
			}
		},
		[submitTest, saveAttempt, user]
	);

	// Called once the user is authenticated (e.g. after returning from the
	// login/signup prompt, from anywhere in the app — see
	// PendingDiagnosticResultSync in App.js) to persist a result that was
	// graded while logged out. Returns the saved result, or null if this call
	// didn't win the claim (nothing pending, or another caller got there first).
	const savePendingResult = useCallback(async () => {
		if (!user) return null;
		const pending = claimPendingResult();
		if (!pending) return null;
		await saveAttempt(pending.benchmarkTestId, pending.result);
		writeLastSyncedResult(pending.result);
		return pending.result;
	}, [user, saveAttempt]);

	// For the benchmark-test page: if savePendingResult() above didn't win the
	// claim because the global sync already saved it, this picks up that
	// result so the page can still show it instead of the intro screen.
	const takeLastSyncedResult = useCallback(() => consumeLastSyncedResult(), []);

	return {
		loading,
		error,
		getActiveBenchmarkTest,
		getAvailableSubjects,
		startTest,
		submitTest,
		saveAttempt,
		takeDiagnosticTest,
		savePendingResult,
		takeLastSyncedResult,
	};
}

// Hook for reading the most recent graded attempt (used on the dashboard
// home page to surface the last set of weak-topic recommendations).
export function useLatestDiagnosticAttempt() {
	const { user } = useAuth();
	const [attempt, setAttempt] = useState(null);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(null);

	const fetchLatest = useCallback(async () => {
		if (!user) {
			setAttempt(null);
			setLoading(false);
			return;
		}

		try {
			setLoading(true);
			const { data, error: supabaseError } = await supabase
				.from("diagnostic_attempts")
				.select("*")
				.eq("user_id", user.id)
				.order("taken_at", { ascending: false })
				.limit(1)
				.maybeSingle();

			if (supabaseError) throw supabaseError;
			setAttempt(data);
		} catch (err) {
			setError(err.message || "Failed to fetch diagnostic attempt");
		} finally {
			setLoading(false);
		}
	}, [user]);

	useEffect(() => {
		fetchLatest();
	}, [fetchLatest]);

	return { attempt, loading, error, refetch: fetchLatest };
}
